import { useState, SyntheticEvent } from 'react';
import RichTextEditor from 'react-rte';
import { richTextConfig } from '../../constants';
import styles from './RichText.scss';
import * as DOMPurify from 'dompurify';

const RichText = ({
  studentNoteMarkdown,
  onChange,
}: RichTextProps): JSX.Element => {
  const [value, setValue] = useState(
    studentNoteMarkdown
      ? RichTextEditor.createValueFromString(studentNoteMarkdown, 'markdown')
      : RichTextEditor.createEmptyValue()
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (newValue: any) => {
    setValue(newValue);
    onChange({
      studentNoteMarkdown: DOMPurify.sanitize(newValue.toString('markdown')),
    });
  };

  // The following handler is required to prevent the RichText events from bubbling up to the parent component
  // and causing any unintended side effects on the Wistia player.
  // The Wistia embed puts a keyup handler on the document object to handle keyboard shortcuts for fullscreen etc.
  // It does a pretty naive test to see if the user is currently focused on a form element by using `element.tagName`
  // and matching on 'select', 'textarea' or 'input' and ignores the keypress in that instance, otherwise executing
  // the shortcut by toggling full screen, pausing, etc.
  // react-rte uses a `div[contenteditable=true]`, so this escape hatch isn't activated and full screen toggle still occurs.

  const handleEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <section className={styles.richText} onKeyUp={handleEvent}>
      <RichTextEditor
        className={styles.config}
        toolbarClassName={styles.toolbar}
        value={value}
        onChange={handleChange}
        toolbarConfig={richTextConfig}
        autoFocus={false}
      />
    </section>
  );
};

export default RichText;

interface Note {
  studentNoteMarkdown: string;
}

interface RichTextProps extends Note {
  onChange: (note: Note) => void;
}
